import { GroupBase, StylesConfig } from "react-select";

import { useTheme } from "../../../../context/theme";
import { useResponsiveProp } from "../../../../hooks";
import useForegroundVariantColor from "../../../../styles/colors/surfaces/useForegroundVariantColor";
import { ResponsiveProp, Width } from "../../../../types";
import { colorWithDisabledOpacity } from "../../../../utils/colorWithOpacity";

type Options = {
  size: "small" | "medium";
  error: boolean;
  menuWidth?: ResponsiveProp<Width>;
  noInputBox?: boolean;
};

const sizes = {
  small: "38px",
  medium: "45px"
};

export default function useGetDropdownStyles<
  Option,
  IsMulti extends boolean,
  Group extends GroupBase<Option>
>({
  size,
  error,
  menuWidth: menuWidthProp = "100%",
  noInputBox
}: Options): StylesConfig<Option, IsMulti, Group> {
  const theme = useTheme();
  const foregroundVariantColor = useForegroundVariantColor("1");

  const backgroundColor = theme.foreground;

  const color = theme["neutral-high-content"];
  const placeholderColor = theme["neutral-medium-content"];

  const borderColor = error
    ? theme["error-high-content"]
    : theme["neutral-high-content"];

  const hover = {
    backgroundColor: foregroundVariantColor
  };

  const menuWidth = useResponsiveProp(menuWidthProp);

  return {
    input: provided => ({
      ...provided,
      color,
      ":active": {
        border: noInputBox ? "none" : provided.border
      },
      ":focus": {
        border: noInputBox ? "none" : provided.border
      }
    }),
    loadingIndicator: provided => ({
      ...provided,
      color
    }),
    loadingMessage: provided => ({
      ...provided,
      color
    }),
    noOptionsMessage: provided => ({
      ...provided,
      color
    }),
    placeholder: (provided, { isDisabled }) => ({
      ...provided,
      color: isDisabled
        ? colorWithDisabledOpacity(placeholderColor)
        : placeholderColor
    }),
    control: (provided, { isDisabled, isFocused }) => ({
      ...provided,
      height: sizes[size],
      backgroundColor: isDisabled
        ? colorWithDisabledOpacity(backgroundColor)
        : backgroundColor,
      borderColor: isDisabled
        ? colorWithDisabledOpacity(borderColor)
        : borderColor,
      ...(isFocused && {
        borderColor: theme["primary-high-content"],
        border: noInputBox ? "none" : provided.border
      }),
      border: noInputBox ? "none" : provided.border,
      background: noInputBox ? "transparent" : provided.background,
      boxShadow: noInputBox ? "none" : provided.boxShadow,
      ":hover": {
        borderColor: !isFocused ? borderColor : theme["primary-high-content"],
        cursor: "pointer"
      }
    }),
    singleValue: (provided, { isDisabled }) => ({
      ...provided,
      color: isDisabled ? colorWithDisabledOpacity(color) : color
    }),
    valueContainer: provided => ({
      ...provided,
      padding: noInputBox ? "0" : provided.padding
    }),
    dropdownIndicator: (
      provided,
      { isDisabled, selectProps: { menuIsOpen } }
    ) => ({
      ...provided,
      color: isDisabled ? colorWithDisabledOpacity(color) : color,
      transition: "transform 0.2s ease",
      ...(menuIsOpen && {
        transform: "rotate(180deg)"
      })
    }),
    option: (provided, { isDisabled, isFocused, isSelected }) => ({
      ...provided,
      backgroundColor,
      ...(isFocused && hover),
      ...(!isDisabled && {
        ":hover": { ...hover, cursor: "pointer" }
      }),
      color: isSelected
        ? theme["primary-high-content"]
        : theme["neutral-high-content"]
    }),
    indicatorSeparator: () => ({}),
    menuList: provided => ({
      ...provided,
      maxHeight: "150px",
      backgroundColor
    }),
    menu: provided => ({ ...provided, width: menuWidth })
  };
}
